import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MakyMascot } from "@/components/brand/MakyMark";
import { LadderBoard } from "@/components/draw/LadderBoard";
import { SlotReel } from "@/components/draw/SlotReel";
import { AppShell } from "@/components/layout/AppShell";
import { PeopleChecklist } from "@/components/people/PeopleChecklist";
import { Button, Card, SegmentedControl, Select, Textarea, TextField } from "@/components/ui";
import { reportDrawUsage, usePeople } from "@/lib/api/queries";
import { copyText } from "@/lib/clipboard";
import {
  assignByLadder,
  buildLadder,
  type DrawAssignment,
  drawWinners,
  formatLadderText,
  formatWinnersText,
  type Ladder,
  padOutcomes,
} from "@/lib/draw";
import {
  LADDER_TIMING,
  LOTS_TIMING,
  nextBoundaryMs,
  prefersReducedMotion,
  revealAt,
  type RevealTiming,
} from "@/lib/reveal";
import { useCurrentWorkspace } from "@/stores/workspace";

type Mode = "lots" | "ladder";

const MODES = [
  { value: "lots", label: "제비뽑기" },
  { value: "ladder", label: "사다리타기" },
];

/** 뽑은 결과. 서버에 보내지 않고 저장하지도 않는다 — 복사해서 단톡방에 붙이면 끝이다.
 *
 * 값은 `handleDraw` 에서 한 번에 다 확정된다. 연출은 이 확정된 값을 언제 보여줄지만
 * 정한다 — 화면이 굴러가는 동안 다시 뽑으면 그건 조작이다.
 */
type Result =
  | { mode: "lots"; title: string; winners: string[]; pool: string[] }
  | {
      mode: "ladder";
      title: string;
      assignments: DrawAssignment[];
      outcomes: string[];
      ladder: Ladder;
    };

function timingOf(mode: Mode): RevealTiming {
  return mode === "lots" ? LOTS_TIMING : LADDER_TIMING;
}

function revealCountOf(result: Result): number {
  return result.mode === "lots" ? result.winners.length : result.assignments.length;
}

export default function DrawPage() {
  const workspaceId = useCurrentWorkspace().workspace?.id ?? "";
  const { data: people } = usePeople(workspaceId);

  const [mode, setMode] = useState<Mode>("lots");
  const [title, setTitle] = useState("");
  const [personIds, setPersonIds] = useState<string[]>([]);
  const [winnerCount, setWinnerCount] = useState("1");
  const [outcomeText, setOutcomeText] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [highlight, setHighlight] = useState<number | null>(null);
  // 연출 진행 시간. `animating` 이 꺼지면 결과가 전부 보인다.
  const [elapsed, setElapsed] = useState(0);
  const [animating, setAnimating] = useState(false);
  // 뽑기 회차. 슬롯 스트립을 remount 해서 처음부터 돌게 하는 데만 쓴다 —
  // `elapsed` 를 key 에 넣으면 첫 착지에서 remount 되어 연출이 끊긴다.
  const [runId, setRunId] = useState(0);

  // 명단 순서 그대로 이름을 뽑는다 — 체크한 순서로 하면 사다리 칸 순서가 사람마다
  // 달라 보여서 "왜 내가 저기 있냐" 는 말이 나온다.
  const picked = (people ?? []).filter((p) => p.active && personIds.includes(p.id));
  const names = picked.map((p) => p.name);
  const label = title.trim() || (mode === "lots" ? "제비뽑기" : "사다리타기");

  const revealCount = result === null ? 0 : revealCountOf(result);
  const timing = timingOf(result?.mode ?? mode);
  const reveal = animating
    ? revealAt(elapsed, revealCount, timing)
    : ({ phase: "done", revealed: revealCount } as const);
  const playing = animating && reveal.phase !== "done";

  // 고정 주기로 틱을 돌리지 않는다 — 움직임은 CSS 가 그리므로 JS 는 착지 순간에만
  // 깨어나면 된다. `elapsed` 를 의존성으로 두어 다시 뽑을 때(0 으로 되돌아갈 때)
  // 반드시 다시 예약된다.
  useEffect(() => {
    if (!animating) return;
    const next = nextBoundaryMs(elapsed, revealCount, timing);
    if (next === null) return;
    const id = setTimeout(() => setElapsed(next), Math.max(0, next - elapsed));
    return () => clearTimeout(id);
  }, [animating, elapsed, revealCount, timing]);

  function startDraw(next: Result) {
    // 뽑은 뒤에 한 번 알린다. 결과도 이름도 보내지 않고 모드와 인원 수만 간다 —
    // 이 기능은 서버를 안 타서, 이 한 줄이 없으면 어드민에서 사용량이 안 보인다.
    reportDrawUsage({ mode: next.mode, size: names.length });
    setResult(next);
    setHighlight(null);
    setElapsed(0);
    setRunId((id) => id + 1);
    // 움직임을 줄여 달라고 설정한 사용자에게는 뜸을 들이지 않는다.
    setAnimating(!prefersReducedMotion());
  }

  function handleDraw() {
    if (names.length < 2) return;
    if (mode === "lots") {
      const count = Math.min(Number(winnerCount) || 1, names.length);
      startDraw({ mode: "lots", title: label, winners: drawWinners(names, count), pool: names });
      return;
    }
    const outcomes = padOutcomes(outcomeText.split("\n"), names.length);
    const ladder = buildLadder(names.length);
    startDraw({
      mode: "ladder",
      title: label,
      outcomes,
      ladder,
      assignments: assignByLadder(names, outcomes, ladder),
    });
  }

  /** 결과를 버린다. 참여자나 모드가 바뀌면 앞의 결과는 다른 얘기다. */
  function clearResult() {
    setResult(null);
    setHighlight(null);
    setAnimating(false);
  }

  async function handleCopy() {
    if (!result) return;
    const text =
      result.mode === "lots"
        ? formatWinnersText(result.title, result.winners)
        : formatLadderText(result.title, result.assignments);
    if (await copyText(text)) toast.success("결과를 복사했어요");
    else toast.error("복사하지 못했어요");
  }

  function handleModeChange(next: string) {
    setMode(next as Mode);
    clearResult();
  }

  return (
    <AppShell title="내기">
      <p className="text-body-2 text-label-alternative">
        커피 사올 사람, 설거지 당번. 다 같이 화면 보면서 한 번에 정해요.
      </p>

      <div className="mt-4">
        <SegmentedControl items={MODES} value={mode} onChange={handleModeChange} />
      </div>

      <Card variant="filled" padding={16} className="mt-4">
        <TextField
          label="무슨 내기예요?"
          placeholder={mode === "lots" ? "예) 커피 사올 사람" : "예) 점심 내기"}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={30}
          helperText="복사한 결과 맨 위에 들어가요."
        />

        <div className="mt-4">
          <PeopleChecklist
            people={people}
            selectedIds={personIds}
            onChange={(ids) => {
              setPersonIds(ids);
              clearResult();
            }}
            emptyText="명단이 비어 있어요. 명단 탭에서 사람을 먼저 추가해 주세요."
          />
        </div>

        {mode === "lots" ? (
          <div className="mt-4">
            <Select
              label="몇 명 뽑을까요?"
              value={winnerCount}
              onChange={(e) => setWinnerCount(e.target.value)}
              options={Array.from({ length: Math.max(names.length, 1) }, (_, i) => ({
                value: String(i + 1),
                label: `${i + 1}명`,
              }))}
              disabled={names.length === 0}
            />
          </div>
        ) : (
          <div className="mt-4">
            <Textarea
              label="사다리 아래에 뭘 놓을까요?"
              placeholder={"커피\n설거지"}
              value={outcomeText}
              onChange={(e) => setOutcomeText(e.target.value)}
              rows={3}
              helperText="한 줄에 하나씩. 사람 수보다 모자라면 나머지는 꽝이 돼요."
            />
          </div>
        )}

        <div className="mt-4">
          <Button
            fullWidth
            // 굴러가는 중에 다시 누르면 연출만 보고 결과를 갈아치우는 셈이 된다.
            disabled={names.length < 2 || playing}
            onClick={handleDraw}
          >
            {result ? "다시 뽑기" : "뽑기"}
          </Button>
          {names.length < 2 && (
            <p className="mt-2 text-caption-1 text-label-alternative">두 명 이상 골라 주세요.</p>
          )}
        </div>
      </Card>

      {result ? (
        <div className="mt-8">
          <div className="flex items-center justify-between gap-2">
            <h2 className="min-w-0 truncate text-label-1 font-semibold text-label-normal">
              {result.title}
            </h2>
            {/* 연출이 끝나기 전에는 복사 버튼을 내지 않는다 — 뜸을 들이는 중에
                결과를 복사해 가면 연출이 아무 뜻이 없다. */}
            {playing ? (
              <Button variant="text" size="small" onClick={() => setAnimating(false)}>
                바로 보기
              </Button>
            ) : (
              <Button variant="text" size="small" onClick={handleCopy}>
                결과 복사
              </Button>
            )}
          </div>

          {result.mode === "lots" ? (
            <Card padding={16} className={playing ? "mt-3" : "mt-3 animate-pop-emphasis motion-reduce:animate-none"}>
              <ul className="flex flex-col gap-1">
                {result.winners.map((winner, i) => (
                  <li key={`${i}-${winner}`}>
                    <SlotReel
                      // 다시 뽑으면 스트립을 새로 만들어 처음부터 돌게 한다.
                      key={`${runId}-${i}`}
                      pool={result.pool}
                      winner={winner}
                      index={i}
                      timing={timing}
                      spinning={animating}
                    />
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-caption-1 text-label-alternative">
                {playing ? "뽑는 중…" : `${names.length}명 중 ${result.winners.length}명`}
              </p>
            </Card>
          ) : (
            <>
              <Card padding={16} className={playing ? "mt-3" : "mt-3 animate-pop-emphasis motion-reduce:animate-none"}>
                <LadderBoard
                  assignments={result.assignments}
                  ladder={result.ladder}
                  outcomes={result.outcomes}
                  timing={timing}
                  revealed={reveal.revealed}
                  playing={playing}
                  highlight={highlight}
                  onPickColumn={(start) => setHighlight((prev) => (prev === start ? null : start))}
                />
              </Card>
              <p className="mt-2 text-caption-1 text-label-alternative">
                {playing ? "사다리 타는 중…" : "이름을 누르면 그 사람 줄만 따로 보여줘요."}
              </p>
              <ul className="mt-3 flex flex-col gap-1">
                {result.assignments.slice(0, reveal.revealed).map((a) => (
                  <li key={a.name} className="animate-pop-in text-body-2 text-label-normal">
                    {`${a.name} → ${a.outcome}`}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* 다 열리면 마스코트가 튀어나온다. 새 자산 없이 기존 마스코트만 쓴다. */}
          {!playing && (
            <div className="mt-4 flex animate-pop-in justify-center motion-reduce:animate-none">
              <MakyMascot size={72} head />
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          <MakyMascot size={96} head />
          <p className="text-body-2 text-label-alternative">
            명단에서 사람을 고르고 뽑기를 눌러 주세요.
          </p>
        </div>
      )}
    </AppShell>
  );
}
